import {
	Fragment,
	useEffect,
	useId,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { rage, useObservable } from '~/rage';

import {
	StyledCircle,
	StyledContainer,
	StyledLevelLabel,
	StyledLevelUpArrow,
	StyledProgressBarContainer,
} from './XPProgressBar.styled';

const PROGRESS_RADIUS = 22;
const PROGRESS_CIRCUMFERENCE = 2 * Math.PI * PROGRESS_RADIUS;

const XPProgressBar: React.FC = () => {
	const arrowSmallId1 = useId();
	const arrowSmallId2 = useId();
	const clipPathId = useId();
	const playerLevel = useObservable(rage.player.getPlayerLevel());
	const level = useMemo(() => playerLevel?.level ?? 0, [playerLevel]);
	const levelProgress = useMemo(
		() => Math.max(0, Math.min(1, playerLevel?.levelProgress ?? 0)),
		[playerLevel],
	);
	// const [visible, setVisible] = useState(false);
	// const earnedExp = useObservable(rage.player.getLastEarnedExp());
	// const [earnedExpString, setEarnedExpString] = useState<string>('');
	const [levelUp, setLevelUp] = useState<boolean>(false);
	const [fillingToComplete, setFillingToComplete] = useState<boolean>(false);
	const [transitioning, setTransitioning] = useState<boolean>(false);
	const [animatedPercentYellow, setAnimatedPercentYellow] = useState<number>(
		() => levelProgress * 100,
	);
	const [animatedPercentWhite, setAnimatedPercentWhite] = useState<number>(
		() => levelProgress * 100,
	);
	const [displayLevel, setDisplayLevel] = useState<number>(level);
	const prevLevelRef = useRef<number>(level);
	const prevLevelProgressRef = useRef<number>(levelProgress);
	const isLevelingUpRef = useRef<boolean>(false);
	const pendingLevelRef = useRef<number | null>(null);

	useLayoutEffect(() => {
		const prevLevel = prevLevelRef.current;
		const prevProgress = prevLevelProgressRef.current;

		if (level > prevLevel) {
			isLevelingUpRef.current = true;
			pendingLevelRef.current = level;
			setDisplayLevel(prevLevel);

			const startProgress = prevProgress * 100;

			const yellowFillDuration = 1000;
			const whiteFillDelay = 4500;
			const whiteFillDuration = 1000;

			setAnimatedPercentYellow(startProgress);
			setAnimatedPercentWhite(startProgress);
			setFillingToComplete(true);

			setTimeout(() => {
				setAnimatedPercentYellow(100);

				setTimeout(() => {
					setAnimatedPercentWhite(100);
				}, whiteFillDelay);
			}, 0);

			const startTransitionTimeout = setTimeout(
				() => {
					setTransitioning(true);
				},
				yellowFillDuration + whiteFillDelay + whiteFillDuration + 100,
			);

			const showLevelUpTimeout = setTimeout(
				() => {
					setFillingToComplete(false);
					setLevelUp(true);
					if (pendingLevelRef.current !== null) {
						setDisplayLevel(pendingLevelRef.current);
					}
				},
				yellowFillDuration + whiteFillDelay + whiteFillDuration + 100 + 250,
			);

			const endTimeout = setTimeout(
				() => {
					setLevelUp(false);
					setTransitioning(false);
					isLevelingUpRef.current = false;
					pendingLevelRef.current = null;
				},
				yellowFillDuration +
					whiteFillDelay +
					whiteFillDuration +
					100 +
					250 +
					12000,
			);

			prevLevelRef.current = level;
			prevLevelProgressRef.current = levelProgress;

			return () => {
				clearTimeout(startTransitionTimeout);
				clearTimeout(showLevelUpTimeout);
				clearTimeout(endTimeout);
				isLevelingUpRef.current = false;
			};
		}

		prevLevelRef.current = level;
		prevLevelProgressRef.current = levelProgress;
	}, [level, levelProgress]);

	// useEffect(() => {
	// 	if (earnedExp) {
	// 		setEarnedExpString(earnedExp.toLocaleString('ru-RU'));
	// 		setVisible(true);

	// 		setTimeout(() => {
	// 			setVisible(false);
	// 		}, 5000);
	// 	}
	// }, [earnedExp]);

	useEffect(() => {
		if (!isLevelingUpRef.current && pendingLevelRef.current === null) {
			setDisplayLevel(level);
		}
	}, [level]);

	const normalProgress = levelProgress * 100;
	const shouldUseAnimated = fillingToComplete || isLevelingUpRef.current;
	const yellowProgressPercent = shouldUseAnimated
		? animatedPercentYellow
		: normalProgress;
	const whiteProgressPercent = shouldUseAnimated
		? animatedPercentWhite
		: normalProgress;
	const isInLevelUpAnimation = levelUp || fillingToComplete;

	return (
		<StyledContainer>
			{levelUp && <StyledLevelUpArrow />}
			<StyledProgressBarContainer>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="50"
					height="50"
					viewBox="0 0 50 50"
					fill="none"
					style={{
						opacity: transitioning && !levelUp ? 0 : 1,
						transform: transitioning && !levelUp ? 'scale(0.95)' : 'scale(1)',
						transition: 'opacity 0.25s ease-out, transform 0.25s ease-out',
					}}
				>
					<circle
						opacity={levelUp ? 1 : 0.25}
						cx="25"
						cy="25"
						r={22}
						stroke={levelUp ? 'none' : 'white'}
						fill={levelUp ? '#FCC612' : 'none'}
						style={{
							transition: 'fill 0.25s ease-out, opacity 0.25s ease-out',
						}}
					/>
					{levelUp ? (
						<Fragment>
							<clipPath id={clipPathId}>
								<circle
									opacity={1}
									cx="25"
									cy="25"
									r="22"
									stroke="none"
									fill="#FCC612"
								/>
							</clipPath>
							<svg
								width="45"
								height="45"
								fill="none"
								x="0"
								y="0"
								clipPath={`url(#${clipPathId})`}
								style={{
									opacity: 1,
									transition: 'opacity 0.25s ease-out',
								}}
							>
								<svg width="24" height="24" fill="none" x="0" y="0">
									<animate
										attributeName="y"
										begin="0.01s"
										dur="6s"
										from="45"
										to="-30"
										repeatCount="indefinite"
									/>
									<path
										d="M0.850494 19.0342C0.172349 19.0342 -0.232139 18.2533 0.144028 17.6703L11.2935 0.390646C11.6296 -0.130215 12.3704 -0.130216 12.7065 0.390645L23.856 17.6703C24.2321 18.2533 23.8277 19.0342 23.1495 19.0342H17.0944C16.6255 19.0342 16.2453 19.427 16.2453 19.9115V29.1227C16.2453 29.6072 15.8652 30 15.3963 30H8.60373C8.13481 30 7.75467 29.6072 7.75467 29.1227V19.9115C7.75467 19.427 7.37453 19.0342 6.9056 19.0342H0.850494Z"
										fill={`url(#${arrowSmallId1})`}
									/>
									<defs>
										<linearGradient
											id={arrowSmallId1}
											x1="12"
											y1="0"
											x2="12"
											y2="30"
											gradientUnits="userSpaceOnUse"
										>
											<stop stopColor="#FFE973" />
											<stop offset="1" stopColor="#FFE973" stopOpacity="0" />
										</linearGradient>
									</defs>
								</svg>
								<svg width="24" height="24" fill="none" x="27" y="45">
									<animate
										attributeName="y"
										begin="3s"
										dur="6s"
										from="45"
										to="-30"
										repeatCount="indefinite"
									/>
									<path
										d="M0.850494 19.0342C0.172349 19.0342 -0.232139 18.2533 0.144028 17.6703L11.2935 0.390646C11.6296 -0.130215 12.3704 -0.130216 12.7065 0.390645L23.856 17.6703C24.2321 18.2533 23.8277 19.0342 23.1495 19.0342H17.0944C16.6255 19.0342 16.2453 19.427 16.2453 19.9115V29.1227C16.2453 29.6072 15.8652 30 15.3963 30H8.60373C8.13481 30 7.75467 29.6072 7.75467 29.1227V19.9115C7.75467 19.427 7.37453 19.0342 6.9056 19.0342H0.850494Z"
										fill={`url(#${arrowSmallId2})`}
									/>
									<defs>
										<linearGradient
											id={arrowSmallId2}
											x1="12"
											y1="0"
											x2="12"
											y2="30"
											gradientUnits="userSpaceOnUse"
										>
											<stop stopColor="#FFE973" />
											<stop offset="1" stopColor="#FFE973" stopOpacity="0" />
										</linearGradient>
									</defs>
								</svg>
							</svg>
							<circle
								opacity="1"
								cx="25"
								cy="25"
								r="19"
								stroke="black"
							></circle>
						</Fragment>
					) : (
						<Fragment>
							<StyledCircle
								style={{
									strokeDasharray: PROGRESS_CIRCUMFERENCE,
									strokeDashoffset:
										PROGRESS_CIRCUMFERENCE * (1 - yellowProgressPercent / 100),
									transition: fillingToComplete
										? 'stroke-dashoffset 1s ease'
										: undefined,
								}}
								cx="25"
								cy="25"
								r={PROGRESS_RADIUS}
								stroke="#FABE2C"
								strokeWidth={5.5}
								strokeLinecap="round"
							/>
							<StyledCircle
								style={{
									strokeDasharray: PROGRESS_CIRCUMFERENCE,
									strokeDashoffset:
										PROGRESS_CIRCUMFERENCE * (1 - whiteProgressPercent / 100),
									transition: fillingToComplete
										? 'stroke-dashoffset 1s ease 4.5s'
										: undefined,
									transitionDelay: isInLevelUpAnimation ? '0s' : '4.5s',
								}}
								cx="25"
								cy="25"
								r={PROGRESS_RADIUS}
								stroke="white"
								strokeWidth={5.5}
								strokeLinecap="round"
							/>
						</Fragment>
					)}
				</svg>
				<StyledLevelLabel
					style={{
						color: levelUp ? '#000000' : '#FFFFFF',
						textShadow: levelUp
							? 'none'
							: '1px 0 0 rgba(0, 0, 0, 0.4),0 1px 0 rgba(0, 0, 0, 0.4),-1px 0 0 rgba(0, 0, 0, 0.4),0 -1px 0 rgba(0, 0, 0, 0.4)',
						transition: 'color 0.3s ease-in-out',
					}}
				>
					{displayLevel}
				</StyledLevelLabel>
			</StyledProgressBarContainer>
			{/* <CSSTransition in={visible} classNames="fade" timeout={750} unmountOnExit>
				{() => (
					<StyledXPTextContainer>
						<TextLabelStroke style={{ color: '#FFFFFF' }}>+</TextLabelStroke>
						<TextLabelStroke style={{ color: '#FABE2C' }}>
							{earnedExpString}
						</TextLabelStroke>
						<StyledStarBorder>
							<StyledStar />
						</StyledStarBorder>
					</StyledXPTextContainer>
				)}
			</CSSTransition> */}
		</StyledContainer>
	);
};

export { XPProgressBar };
